import React, { Fragment, useState } from 'react';
import {
  Stack,
  TableCell,
  Box,
  IconButton,
  Dialog,
  DialogContent,
  Typography,
  ClickAwayListener,
} from '@mui/material';
import Info from '../../../../listItem/table/components/Info.component';
import Detail from '../../../../listItem/table/components/Detail.component';
import ControlAutocomplete from '../../../../listItem/table/components/ControlAutocomplete.component';
import ControlTextField from '../../../../listItem/table/components/ControlTextField.component';
import SliderComponent from '../../../../common/Slider.component';
import CardMedia from '../../../../common/CardMedia.component';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';
import { AvailableType } from '../../../../../module/listItem/conf/Data.conf';
import IconsCrud from '../../../../common/IconsCrud.component';
import ImageIcon from '@mui/icons-material/Image';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadButton from '../../../../form/components/Upload.component';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmDialog from '../../../../common/Confirm.component';

const TableRow = ({
  item,
  onChange = () => {},
  editable,
  currentState,
  handleEditable = () => {},
  onSaveAction = () => {},
  handleOpen = () => {},
  handleFileChange = () => {},
  handleFileDelete = () => {},
  remainingFiles,
  crudDisabled,
  cancelEdit = () => {},
}) => {
  const [openImageModal, setOpenImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const carouselHeight = '50vh';

  const handleClickAway = () => {
    if (editable?.[item?.id]) {
      cancelEdit(item.id);
    }
  };

  const handleOpenImageModal = (imageUrl, index = 0) => {
    setSelectedImage(imageUrl);
    setCurrentImageIndex(index);
    setOpenImageModal(true);
  };
  const handleCloseImageModal = () => {
    setOpenImageModal(false);
    setSelectedImage(null);
  };

  const handleOpenDeleteConfirm = (itemId, fileId, e) => {
    e.stopPropagation();
    setFileToDelete({ itemId, fileId });
    setOpenDeleteConfirm(true);
  };
  const handleCloseDeleteConfirm = () => {
    setOpenDeleteConfirm(false);
    setFileToDelete(null);
  };
  const confirmFileDelete = () => {
    if (fileToDelete) {
      handleFileDelete(fileToDelete.itemId, fileToDelete.fileId);
    }
    setOpenDeleteConfirm(false);
  };

  const attachmentsToShow =
    editable?.[item?.id] && remainingFiles
      ? item?.attributes?.attachment?.data?.filter((attachment) =>
          remainingFiles.includes(attachment.id)
        )
      : item?.attributes?.attachment?.data;

  const getImagesUrls = () => {
    if (!attachmentsToShow || attachmentsToShow.length === 0) return [];

    const base_url = process.env.REACT_APP_BASE_URL.split('/api')[0];
    return attachmentsToShow.map((attachment) => {
      const url = _.get(attachment?.attributes, 'url');
      return `${base_url}${url}`;
    });
  };

  const imageUrls = getImagesUrls();

  const handlePrevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? imageUrls.length - 1 : prevIndex - 1
    );
    setSelectedImage(
      imageUrls[
        currentImageIndex === 0 ? imageUrls.length - 1 : currentImageIndex - 1
      ]
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === imageUrls.length - 1 ? 0 : prevIndex + 1
    );
    setSelectedImage(
      imageUrls[
        currentImageIndex === imageUrls.length - 1 ? 0 : currentImageIndex + 1
      ]
    );
  };

  const rowContent = (
    <Box display={'contents'}>
      <TableCell>
        <Stack spacing={1}>
          <Detail item={item} />
          <Info
            onChange={onChange}
            item={item}
            currentState={currentState}
            editable={editable}
          />
        </Stack>
      </TableCell>

      <TableCell width={250} sx={{ verticalAlign: 'top' }}>
        {editable?.[item?.id] && (
          <Box mb={2}>
            <UploadButton
              label='Photo'
              name={`attachment_${item.id}`}
              multiple={true}
              onChange={(files) => handleFileChange(files)}
            />
          </Box>
        )}
        {attachmentsToShow && attachmentsToShow.length > 0 ? (
          <Box mb={1} mt={1}>
            {!editable?.[item?.id] ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
                onClick={() =>
                  attachmentsToShow.length > 0 &&
                  handleOpenImageModal(imageUrls[0], 0)
                }
              >
                <ImageIcon sx={{ color: '#3f51b5' }} />
                <Typography variant='body2' sx={{ ml: 1 }}>
                  {attachmentsToShow.length}{' '}
                  {attachmentsToShow.length === 1 ? 'image' : 'images'}
                </Typography>
              </Box>
            ) : (
              <SliderComponent>
                {attachmentsToShow.map((attachment, index) => {
                  const base_url =
                    process.env.REACT_APP_BASE_URL.split('/api')[0];
                  const url = _.get(attachment?.attributes, 'url');
                  const fullImageUrl = `${base_url}${url}`;

                  return (
                    <Box pr={1} key={uuidv4()} sx={{ position: 'relative' }}>
                      <Box
                        onClick={() =>
                          handleOpenImageModal(fullImageUrl, index)
                        }
                        sx={{ cursor: 'pointer' }}
                      >
                        <CardMedia image={fullImageUrl} />
                      </Box>
                      <IconButton
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 6,
                          margin: '5px',
                          bgcolor: 'rgba(255, 255, 255, 0.8)',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 1)',
                          },
                          width: '20px',
                          height: '20px',
                        }}
                        size='small'
                        onClick={(e) =>
                          handleOpenDeleteConfirm(item.id, attachment.id, e)
                        }
                      >
                        <DeleteIcon fontSize='small' color='error' />
                      </IconButton>
                    </Box>
                  );
                })}
              </SliderComponent>
            )}
          </Box>
        ) : (
          !editable?.[item?.id] && '-'
        )}
      </TableCell>
      <TableCell>
        <ControlTextField
          item={item}
          editable={editable}
          currentState={currentState}
          onChange={onChange}
          name='quantity'
          label='Quantity'
          visibleText={false}
          sx={{ marginTop: 0 }}
          type='number'
        />
      </TableCell>

      <TableCell>
        <ControlAutocomplete
          item={item}
          editable={editable}
          options={AvailableType}
          currentState={currentState}
          onChange={onChange}
          name='available'
          label='Available'
          required={true}
          disableClearable={true}
        />
      </TableCell>

      <TableCell>
        <IconsCrud
          sx={{ display: 'contents' }}
          editable={editable?.[item?.id]}
          onSaveAction={() => onSaveAction(item)}
          handleEditable={() => handleEditable(item)}
          disabledEdit={
            crudDisabled ? crudDisabled : item?.disabledEdit || false
          }
          disabledDelete={
            crudDisabled ? crudDisabled : item?.disabledDelete || false
          }
          handleOpen={() => handleOpen(item)}
        />
      </TableCell>
      <Dialog
        open={openImageModal}
        onClose={handleCloseImageModal}
        maxWidth='md'
        fullWidth
      >
        <DialogContent
          sx={{ position: 'relative', padding: '16px', height: carouselHeight }}
        >
          <IconButton
            onClick={handleCloseImageModal}
            sx={{
              position: 'absolute',
              top: 0,
              right: 8,
              color: 'black',
            }}
          >
            <CloseIcon />
          </IconButton>
          {imageUrls.length > 0 && (
            <>
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                <img
                  src={selectedImage}
                  alt='Full Size'
                  style={{
                    maxWidth: '90%',
                    maxHeight: '90%',
                    objectFit: 'contain',
                    borderRadius: '6px',
                  }}
                />
              </Box>
              {imageUrls.length > 1 && (
                <>
                  <IconButton
                    onClick={handlePrevImage}
                    sx={{
                      position: 'absolute',
                      left: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'black',
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    <ArrowBackIosIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleNextImage}
                    sx={{
                      position: 'absolute',
                      right: '3px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'black',
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    <ArrowForwardIosIcon sx={{ textAlign: 'center' }} />
                  </IconButton>
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: '10px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      color: 'white',
                      padding: '5px 10px',
                      borderRadius: '12px',
                    }}
                  >
                    {currentImageIndex + 1} / {imageUrls.length}
                  </Box>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={openDeleteConfirm}
        onClose={handleCloseDeleteConfirm}
        title='Are you sure you want to delete this image?'
        description='The image will be deleted permanently. You cannot undo this action.'
        cancelAction={handleCloseDeleteConfirm}
        confirmAction={confirmFileDelete}
      />
    </Box>
  );
  if (editable?.[item?.id]) {
    return (
      <ClickAwayListener onClickAway={handleClickAway}>
        {rowContent}
      </ClickAwayListener>
    );
  }
  return rowContent;
};

export default TableRow;
